import { useTheme } from './useTheme';
import {
  ALLOY_THEME_ID,
  WEAVE_THEME_ID,
  ALLOY_WEAVE_THEME_ID,
  ALLOY_WEAVE3_THEME_ID,
} from '../constants';
import {
  alloyComponentIcons,
  weaveComponentIcons,
  ComponentIcons,
  ComponentName,
} from '../icons';

/**
 * Maps a theme id to its icon set. `ALLOY_WEAVE_THEME_ID` intentionally reuses
 * `alloyComponentIcons`: the combined theme is the React 17/18 compatibility
 * layer over Alloy and is expected to be visually identical to it for icons.
 * `WEAVE_THEME_ID` and `ALLOY_WEAVE3_THEME_ID` swap in `weaveComponentIcons`:
 * Weave 3 is a Weave theme, so it takes the Weave iconography. This convention
 * is shared by all weave-migrated components (see DSGN-2187, Avatar).
 *
 * `ALLOY_WEAVE3_THEME_ID` takes `weaveComponentIcons`: W3 is a Weave-family
 * theme, not a compatibility layer. Without a row here the lookup returns
 * `undefined` and every icon consumer throws under W3 (DSGN-2554).
 *
 * An unrecognised id falls back to `alloyComponentIcons` rather than throwing.
 * A consumer-supplied theme that satisfies `isCombinedTheme` passes through
 * `useTheme` with its own id intact, so an id outside this map is reachable
 * from application code — and a missing id already defaults to Alloy above, so
 * this extends the same policy rather than inventing one. A genuinely missing
 * row still fails loudly in the registry tests.
 */
const componentIconsMap: Record<string, ComponentIcons> = {
  [ALLOY_THEME_ID]: alloyComponentIcons,
  [WEAVE_THEME_ID]: weaveComponentIcons,
  [ALLOY_WEAVE_THEME_ID]: alloyComponentIcons,
  [ALLOY_WEAVE3_THEME_ID]: weaveComponentIcons,
};

export function useComponentIcons(): ComponentIcons;

export function useComponentIcons<T extends ComponentName>(
  componentName: T,
): ComponentIcons[T];

export function useComponentIcons<T extends ComponentName>(
  componentName?: T,
): ComponentIcons | ComponentIcons[T] {
  const { id: themeId = ALLOY_THEME_ID } = useTheme();
  const icons = componentIconsMap[themeId] ?? alloyComponentIcons;
  return componentName ? icons[componentName] : icons;
}
